<template>
  <div class="TaskPanel">
    <!-- Single full-screen loader (app-wide CommonOverlay pattern) covers the panel until ALL
         initial data has loaded, so the user never sees a half-loaded panel (e.g. the top
         cards visible while OD imaging categories are still loading). -->
    <CommonOverlay :show="loading" title="Loading Settings" message="Please wait..." />
    <b-overlay :show="saving" rounded="sm">
      <section class="task-panel-section">
        <div class="details-header">
          <h4 class="details-title">SMS Conversation Settings</h4>
          <div class="details-header-actions">
            <button type="button" class="btn btn-success btn-sm mr-2" @click="save" :disabled="saving" data-test="sms-settings-save">
              <b-spinner v-if="saving" small class="mr-2"></b-spinner>
              <i v-else class="fa fa-save mr-2"></i>
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="closePanel" :disabled="saving">Cancel</button>
          </div>
        </div>

        <div class="card panel task-details-main">
          <div class="task-details-main-item">
            <label class="detail-label-block">Notifications</label>
            <b-form-checkbox v-model="soundPushEnabled" switch :disabled="saving" data-test="sms-sound-push-toggle">
              <span class="switch-label">Play sound &amp; desktop notifications for new patient SMS</span>
            </b-form-checkbox>
          </div>

          <div v-if="soundPushEnabled" class="task-details-main-item">
            <NotificationSoundPicker
              v-model="notificationSound"
              label="SMS notification sound"
              hint="Click ▶ to preview. This sound plays when a patient sends you an SMS."
            />
          </div>

          <div class="task-details-main-item">
            <NotificationHelp :testSoundFile="notificationSound" />
          </div>
        </div>

        <div class="card panel task-details-main">
          <div class="task-details-main-item">
            <label class="detail-label-block" for="sms-photo-imaging-category">Save patient-sent images to Open Dental</label>
            <p class="setting-hint">
              When a patient texts you a photo, automatically save it to Open Dental under the
              imaging category you pick here. Leave empty to keep photos in the chat only.
            </p>
            <select
              id="sms-photo-imaging-category"
              class="form-control"
              v-model="smsPhotoImagingCategory"
              :disabled="saving"
              data-test="sms-photo-imaging-category"
            >
              <option value="">--Disabled (don't save patient SMS photos to OD)--</option>
              <option v-for="cat in formCategories" :key="cat.DefNum" :value="cat.DefNum">
                {{ cat.ItemName }}
              </option>
            </select>
          </div>
        </div>

        <!-- WhatsApp settings are only shown once Teamio has enabled WhatsApp for this site
             (WHATSAPP_ENABLED='true'). Until then the whole section is hidden, so practices
             that aren't on the WhatsApp pilot never see WhatsApp controls. -->
        <div v-if="siteWhatsAppEnabled" class="card panel task-details-main">
          <div class="task-details-main-item">
            <label class="detail-label-block">WhatsApp messaging</label>
            <!-- WhatsApp-only office (SMS_OUTBOUND_DISABLED): the server refuses to turn WhatsApp off
                 (409) — it is the only way this office can message patients. Lock it and say why,
                 in the server's own words, instead of letting a save fail. -->
            <p v-if="whatsappLocked" class="setting-hint" data-test="whatsapp-locked-reason">
              {{ whatsappRequiredMessage }}
            </p>
            <p v-else class="setting-hint">
              WhatsApp is enabled for this practice as an additional channel alongside SMS.
              SMS keeps working exactly as before — this only adds WhatsApp where a WhatsApp
              conversation is used. Turning it off here disables WhatsApp for the practice.
            </p>
            <b-form-checkbox v-model="whatsappEnabled" switch :disabled="saving || (whatsappLocked && whatsappEnabledInitial)" data-test="whatsapp-enabled-toggle">
              <span class="switch-label">WhatsApp enabled for this site</span>
            </b-form-checkbox>
          </div>

          <!-- READ-ONLY on purpose (2026-09-11). This is the number Teamio's WhatsApp messages go out
               from and patients' replies come back to; onboarding sets it (register-sender copies
               the site's own Teamio number into GENERAL_WHATSAPP_PHONE). It used to be a free-text
               box labelled "the practice's WhatsApp Business number" — and a practice that ALSO
               keeps its own WhatsApp Business app number would naturally type that one in, which
               silently breaks sends and strands patients' replies. The backend now refuses such a
               change too (conversations-service checkWhatsAppPhoneWrite). -->
          <div v-if="whatsappEnabled" class="task-details-main-item" data-test="teamio-whatsapp-number">
            <label class="detail-label-block">Teamio WhatsApp number</label>
            <p v-if="generalWhatsappPhone" class="teamio-whatsapp-number" data-test="teamio-whatsapp-number-value">{{ generalWhatsappPhone }}</p>
            <p v-else class="setting-hint" data-test="teamio-whatsapp-number-missing">
              Not set up yet. Teamio adds this number when WhatsApp setup is finished.
            </p>
            <p class="setting-hint">
              Patients get your WhatsApp messages from Teamio on this number, and their replies come
              back to Teamio. Teamio set this number up for your practice, so it can&rsquo;t be
              changed here. If it ever needs to change, contact Teamio.
            </p>
            <p class="setting-hint mb-0">
              If your team also uses the WhatsApp Business app, that app has its own number. It is
              separate from this one and keeps working in the app as before.
            </p>
          </div>
        </div>

        <!-- Discovery cross-link: the signup wizard lives in My Account, not Settings.
             Only for sites that DON'T have WhatsApp yet (v-else on the card above) — a
             practice that already has it never sees a "set it up" pointer. -->
        <div v-else-if="selfStartEnabled" class="card panel task-details-main">
          <div class="task-details-main-item">
            <label class="detail-label-block">WhatsApp messaging</label>
            <!-- The CARD stays visible to every role — discovery is the point, and the people
                 reading Conversations settings are usually the ones handling patient messaging.
                 Only the CALL TO ACTION is role-aware: the wizard lives on the My Account →
                 WhatsApp tab, which is Admin/Manager and above, so linking an Employee there
                 hands them a tab that never renders the option. They get told who to ask
                 instead of a dead end. -->
            <p v-if="canRunWhatsAppWizard" class="setting-hint mb-0">
              Want to message patients on WhatsApp? Set it up in
              <router-link :to="{ path: '/app/my-account', query: { tab: 'whatsapp' } }">My Account &rarr; WhatsApp</router-link>.
            </p>
            <p v-else class="setting-hint mb-0">
              Want to message patients on WhatsApp? Ask your practice admin about turning it on.
            </p>
          </div>
        </div>
      </section>
    </b-overlay>
  </div>
</template>

<script>
import axios from 'axios';
import NotificationSoundPicker from '@/components/NotificationSoundPicker.vue';
import NotificationHelp from '@/components/NotificationHelp.vue';
import CommonOverlay from '@/components/CommonOverlay.vue';
import { getPrefs, setPref } from '@/utils/notificationPrefs';
import { isSmsOutboundDisabled, WHATSAPP_REQUIRED_MESSAGE } from '@/utils/siteCapability';
import { extractApiError } from '@/utils/apiError';

export default {
  name: 'ConversationsSettings',
  components: { NotificationSoundPicker, NotificationHelp, CommonOverlay },

  data() {
    return {
      // Full-panel initial-load gate — true until ALL async loads in created() finish.
      // Drives the CommonOverlay so the panel is never shown half-loaded.
      loading: true,
      soundPushEnabled: false,
      notificationSound: 'simple-notification-152054.mp3',
      saving: false,
      // OD imaging category for SMS photos (site-level setting; empty = feature off)
      smsPhotoImagingCategory: '',
      smsPhotoImagingCategoryInitial: '',
      formCategories: [],
      // WhatsApp channel (site-level settings; additive — does not affect SMS)
      whatsappEnabled: false,
      whatsappEnabledInitial: false,
      // The Teamio WhatsApp number — displayed, never edited (see the template).
      generalWhatsappPhone: '',
    };
  },

  computed: {
    userID() {
      return this.$store.state.customer.userID;
    },
    siteID() {
      return this.$store.state.customer.siteID;
    },
    // Site settings loaded on app init via the customer module. The canonical
    // store path is `state.customer.settings` (an array of {Property, PropertyValue});
    // there is no `state.customer.allSettings`. Key the array by Property here so
    // loadSiteSetting() can read SMS_PHOTO_IMAGING_CATEGORY back on reload.
    // (This component doesn't use the global mixin, which exposes the same keyed map.)
    allSettings() {
      const settings = this.$store.state.customer.settings || [];
      return settings.reduce((acc, s) => {
        acc[s.Property] = s;
        return acc;
      }, {});
    },
    // Whether Teamio has enabled WhatsApp for this site (the saved WHATSAPP_ENABLED setting).
    // Gates the whole WhatsApp settings card so non-pilot practices never see WhatsApp controls.
    siteWhatsAppEnabled() {
      const v = this.allSettings && this.allSettings.WHATSAPP_ENABLED && this.allSettings.WHATSAPP_ENABLED.PropertyValue;
      return String(v).toLowerCase() === 'true';
    },
    // Whether self-start signup exists to point at. Same flag that reveals the
    // My Account → WhatsApp tab, so this link can never lead somewhere that isn't there.
    selfStartEnabled() {
      return process.env.VUE_APP_WHATSAPP_SELF_START === 'true';
    },
    // The flag above proves the TAB exists; this proves THIS USER can reach it. The My Account
    // → WhatsApp tab admits Admin, Super Admin and Manager (MyAccount.vue canRunWhatsAppWizard —
    // must stay identical, AGREEMENT test), so showing its link to an Employee sends them to a
    // tab that never renders the option.
    // Read straight off the store, like every other value in this component — it deliberately
    // avoids the global mixin (see the allSettings comment above), whose created() hook would
    // refetch settings + employees on every mount. `user` hydrates from localStorage and is
    // legitimately null on a cold load, so it is guarded rather than dereferenced.
    // Texting is switched off for this office (SMS_OUTBOUND_DISABLED) — WhatsApp is its only
    // channel, so it can't be turned off here. siteCapability is the one parser of the setting.
    whatsappLocked() {
      return isSmsOutboundDisabled(this.allSettings);
    },
    whatsappRequiredMessage() {
      return WHATSAPP_REQUIRED_MESSAGE;
    },
    canRunWhatsAppWizard() {
      const user = this.$store.state.customer.user;
      const role = user && user.Role;
      return role === 'Admin' || role === 'Super Admin' || role === 'Manager';
    },
  },

  async created() {
    this.loadFromPrefs();
    // Hold the panel behind a single loader until BOTH async loads complete, so it never
    // renders piecemeal (top cards visible while OD imaging categories are still loading).
    // `finally` guarantees the overlay clears even if a load fails — the user is never trapped.
    try {
      await Promise.all([this.loadSiteSetting(), this.loadFormCategories()]);
    } finally {
      this.loading = false;
    }
  },

  methods: {
    loadFromPrefs() {
      const prefs = getPrefs(this.userID);
      this.soundPushEnabled = prefs.sms.soundPush;
      this.notificationSound = prefs.sms.sound;
    },

    async loadSiteSetting() {
      // SMS_PHOTO_IMAGING_CATEGORY is a teamio.settings row. May be absent on sites
      // that haven't enabled the feature — that's the default (feature off).
      //
      // Refresh from the DB first. The cached settings array (state.customer.settings,
      // hydrated from localStorage) is only populated at login/bootstrap, and the
      // save endpoint writes the DB directly without touching it — so a category
      // saved after login would otherwise stay invisible (dropdown shows "Disabled")
      // until the next full login. getSettings re-runs the same Property/PropertyValue
      // query the bootstrap uses and re-commits + re-caches the array.
      try {
        await this.$store.dispatch('customer/getSettings', { siteID: this.siteID });
      } catch (e) {
        console.error('ConversationsSettings: failed to refresh site settings:', e);
      }
      const setting = this.allSettings && this.allSettings.SMS_PHOTO_IMAGING_CATEGORY;
      const value = setting && setting.PropertyValue ? String(setting.PropertyValue) : '';
      this.smsPhotoImagingCategory = value;
      this.smsPhotoImagingCategoryInitial = value;

      // WhatsApp settings (additive). WHATSAPP_ENABLED is stored as the string 'true'/'false'.
      const waEnabled = this.allSettings && this.allSettings.WHATSAPP_ENABLED;
      this.whatsappEnabled = !!(waEnabled && String(waEnabled.PropertyValue).toLowerCase() === 'true');
      this.whatsappEnabledInitial = this.whatsappEnabled;
      // Display only — see the read-only block in the template for why it is never editable.
      const waPhone = this.allSettings && this.allSettings.GENERAL_WHATSAPP_PHONE;
      this.generalWhatsappPhone = waPhone && waPhone.PropertyValue ? String(waPhone.PropertyValue) : '';
    },

    async loadFormCategories() {
      // Reuses the same endpoint the form-builder uses to populate its imaging-category dropdown.
      // Returns rows of { DefNum, ItemName } from the site's local OD `definition` table.
      // (Initial-load spinner is handled by the panel-wide `loading` overlay in created().)
      try {
        const response = await this.$store.dispatch('forms/getFormCategories', { siteID: this.siteID });
        // forms-service _getFormCategories returns array directly
        this.formCategories = Array.isArray(response) ? response : (response && response.data) || [];
      } catch (e) {
        console.error('ConversationsSettings: failed to load OD imaging categories:', e);
        this.$toast.error('Could not load Open Dental imaging categories.');
      }
    },

    async save() {
      // Never post a change the server will refuse (and never let a stale panel try).
      if (this.whatsappLocked && !this.whatsappEnabled && this.whatsappEnabledInitial) {
        this.$toast.error(WHATSAPP_REQUIRED_MESSAGE);
        this.whatsappEnabled = true;
        return;
      }
      this.saving = true;
      try {
        // Per-user notification prefs (localStorage) — unchanged from original
        setPref(this.userID, 'sms', {
          soundPush: this.soundPushEnabled,
          sound: this.notificationSound,
        });

        // Site-level setting — only POST if it changed (avoid unnecessary writes)
        if (this.smsPhotoImagingCategory !== this.smsPhotoImagingCategoryInitial) {
          await axios.post('/save-sms-photo-imaging-category', {
            siteID: this.siteID,
            defNum: this.smsPhotoImagingCategory ? parseInt(this.smsPhotoImagingCategory, 10) : 0,
          });
          this.smsPhotoImagingCategoryInitial = this.smsPhotoImagingCategory;
        }

        // WhatsApp on/off — only POST if it changed. The number is deliberately NOT sent: it is
        // read-only here, and omitting it makes the backend take its flag-only write, which can
        // never overwrite or blank the Teamio WhatsApp number.
        if (this.whatsappEnabled !== this.whatsappEnabledInitial) {
          await axios.post('/save-whatsapp-settings', {
            siteID: this.siteID,
            whatsappEnabled: this.whatsappEnabled ? 'true' : 'false',
          });
          this.whatsappEnabledInitial = this.whatsappEnabled;
        }

        this.$toast.success('SMS settings saved!');
        this.$emit('closePanel', {});
      } catch (e) {
        console.error('ConversationsSettings save failed:', e);
        // The server's own sentence when it gave one (e.g. the WhatsApp-only 409); the old
        // generic line made a refusal read like a glitch worth retrying.
        this.$toast.error(extractApiError(e, 'Failed to save SMS settings. Please try again.'));
      } finally {
        this.saving = false;
      }
    },

    closePanel() {
      this.$emit('closePanel', {});
    },
  },
};
</script>

<style lang="scss" scoped>
.TaskPanel {
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
  color: #334155;
  background-color: #f8fafc;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.task-panel-section {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  flex: 1;
}

.details-header {
  margin-bottom: 0.875rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.details-header-actions {
  display: flex;
  align-items: center;
}

.details-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.task-details-main {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 1rem;
  padding: 1rem;
}

.task-details-main-item {
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.detail-label-block {
  display: block;
  font-weight: 600;
  color: #475569;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.switch-label {
  color: #0f172a;
  font-size: 0.9375rem;
}

.setting-hint {
  color: #64748b;
  font-size: 0.8125rem;
  margin-top: 0.25rem;
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

/* The read-only Teamio WhatsApp number: plain text, weighted so it reads as a value, not a hint. */
.teamio-whatsapp-number {
  color: #0f172a;
  font-size: 0.9375rem;
  font-weight: 600;
  margin: 0.25rem 0 0.25rem;
  overflow-wrap: anywhere;
}

.form-control {
  display: block;
  width: 100%;
  padding: 0.4375rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 0.375rem;
  border: 1px solid #cbd5e1;
  background-color: #fff;
  color: #0f172a;
}

.form-control:disabled {
  background-color: #f1f5f9;
  cursor: not-allowed;
}
</style>
